import Alert from '@/components/Alert/Alert'
import { Box } from '@/components/ui/box'
import { Divider } from '@/components/ui/divider'
import { HStack } from '@/components/ui/hstack'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import { selectCoverById } from '@/redux/slices/coverSlice'
import { useAppSelector } from '@/redux/store'
import { BaseFileEnum, Scene, type FolderCoverFragment, type FormCoverFragment } from '@/typings/domain'
import dayjs from 'dayjs'
import { Link } from 'expo-router'
import { Fragment } from 'react'
import FolderIcon from './FolderIcon'
import FormIcon from './FormIcon'

const UNSUPPORTED_SCENES = [Scene.CustomerAcquisition, Scene.Vote]

type CoverProps = {
  id: string
  showBorder: boolean
}

export default function Cover({ id, showBorder }: CoverProps) {
  const cover = useAppSelector(state => selectCoverById(state.cover, id))

  if (!cover) return null

  return cover.type === BaseFileEnum.Form ? (
    <FormItem form={cover as FormCoverFragment} showBorder={showBorder} />
  ) : (
    <FolderItem folder={cover as FolderCoverFragment} />
  )
}

type FormItemProps = {
  form: FormCoverFragment
  showBorder: boolean
}

function FormItem({ form, showBorder }: FormItemProps) {
  const isUnsupported = UNSUPPORTED_SCENES.includes(form.scene)
  const isDisabled = !!form.disabled

  const handleUnsupportedPress = () => {
    Alert.alert('暂不支持该场景', '若你需要编辑表单，请前往 PC 端编辑。')
  }

  const handleDisabledPress = () => {
    Alert.alert('表单已被封停')
  }

  if (isDisabled) {
    return (
      <Pressable className="opacity-50" onPress={handleDisabledPress}>
        <FormItemContent
          scene={form.scene}
          name={form.subname || form.name}
          descriptionItems={[`${dayjs(form.createdAt).format('YYYY-MM-DD')} 创建`, `${form.entriesCount} 条数据`]}
          showBorder={showBorder}
        />
      </Pressable>
    )
  }

  if (isUnsupported) {
    return (
      <Pressable className="opacity-50" onPress={handleUnsupportedPress}>
        <FormItemContent
          scene={form.scene}
          name={form.subname || form.name}
          descriptionItems={[`${dayjs(form.createdAt).format('YYYY-MM-DD')} 创建`, `${form.entriesCount} 条数据`]}
          showBorder={showBorder}
        />
      </Pressable>
    )
  }

  return (
    <Link href={`/forms/${form.token}`} asChild>
      <Pressable className="data-[active=true]:bg-background-100">
        <FormItemContent
          scene={form.scene}
          name={form.subname || form.name}
          descriptionItems={[`${dayjs(form.createdAt).format('YYYY-MM-DD')} 创建`, `${form.entriesCount} 条数据`]}
          showBorder={showBorder}
        />
      </Pressable>
    </Link>
  )
}

type FolderItemProps = {
  folder: FolderCoverFragment
}

function FolderItem({ folder }: FolderItemProps) {
  return (
    <Box className="px-6 py-2">
      <Link href={`/folders/${folder.token}`} asChild>
        <Pressable className="rounded-lg bg-background-50 data-[active=true]:bg-background-100">
          <HStack space="lg" className="p-4 items-center">
            <FolderIcon color={folder.ensuredSymbol?.backgroundColor} />
            <Text size="body" className="text-label flex-1 min-w-0">
              {folder.name}
            </Text>
          </HStack>
        </Pressable>
      </Link>
    </Box>
  )
}

type FormItemContentProps = {
  scene: Scene
  name: string
  descriptionItems: string[]
  showBorder: boolean
}

function FormItemContent({ scene, name, descriptionItems, showBorder }: FormItemContentProps) {
  return (
    <HStack className="items-justify px-2">
      <Box className="p-4">
        <FormIcon scene={scene} />
      </Box>
      <VStack
        space="sm"
        className={`flex-1 justify-center py-2 mr-4 ${showBorder ? 'border-b border-background-100' : ''}`}
      >
        <Text size="body" className="text-label">
          {name}
        </Text>
        {descriptionItems.length > 0 && (
          <HStack space="sm" className="items-center">
            {descriptionItems.map((desc, index) => (
              <Fragment key={index}>
                {index > 0 && <Divider orientation="vertical" className="h-3/5" />}
                <Text size="subhead" className="text-secondary-label">
                  {desc}
                </Text>
              </Fragment>
            ))}
          </HStack>
        )}
      </VStack>
    </HStack>
  )
}
